import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as schema from '../schema'

import type { TypeParams } from './types'

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(schema.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }

      const { modelId, dimensionId } = context

      const source = modelId ?? dimensionId

      if (!source) {
        // Cannot return anything without a valid entityId or dimensionId
        return rxjs.of([])
      }

      return this._schemaFeature.selectors.schema.asObservable.pipe(
        rxjs.filter(TypeChecker.isNotNullish.raw),
        rxjs.map(_schema => _schema.getAttributesOf(source)),
      )
    },
  )

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      // Name
      ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
      // Type
      {
        getValueFrom: attribute => {
          const { dimensionId, enumId, isMultiselect, structureId } =
            attribute.type as FlattenUnion<typeof attribute.type>
          if (
            ents.core.common.value.types.Intrinsic.isIntrinsic(attribute.type)
          ) {
            return attribute.type
          }
          // TODO: for enums, structures, and references, get the names for the
          // associated models so we can create a more use-friendly value
          if (enumId) {
            return `${isMultiselect ? 'MULTI-SELECT' : 'SINGLE SELECT'} ENUM: ${enumId}`
          }
          if (structureId) {
            return `STRUCTURE: ${structureId}`
          }
          if (dimensionId) {
            return `REF: ${dimensionId}`
          }
          // This should never happen
          return 'UNKNOWN'
        },
        id: 'type',
        name: 'Data Type',
      },
    ] satisfies readonly ec.models.Attribute<TypeParams>[])

  /** @inheritdoc */
  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    if (!context) {
      return rxjs.of(undefined)
    }

    const { modelId, dimensionId } = context
    const attributeId = id as ents.core.dms.models.Attribute.Id

    const source = modelId ?? dimensionId

    if (!source) {
      return rxjs.of(undefined)
    }

    return this._schemaFeature.selectors.schema.asObservable.pipe(
      rxjs.filter(TypeChecker.isNotNullish.raw),
      rxjs.map(_schema =>
        ents.core.dms.models.Attribute.isOn(attributeId, source)
          ? _schema.getAttribute(attributeId)
          : undefined,
      ),
    )
  }
}
